import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet";
import styles from "./menu.module.scss";

import { useLocation, matchPath, useNavigate } from "react-router-dom";
import useAppModernisation from "../../hooks/useAppModernisation";

import TablesAndFigures from "../../features/tables-and-figures/appmode/TablesAndFigures";
import PatientInfoApp from "../../features/patientInfo/PatientInfoApp";
import AcknowledgmentForAppMode from "../AcknowledgementModal/appMode/AcknowledgmentForAppMode";
import ExpertGroupsForAppMode from "../ExpertGroups/appmode/ExpertGroupsForAppMode";
import FAQApp from "../FAQ/FAQApp";
import GuidelineUpdatesPage from "../../features/guideline/updates/GuidelineUpdatesPage";
import { QuickLinkItem } from "../QuickLink/QuickLinkItem";
const MenuBar = lazy(() => import("../menuBar/menuBar"));
const BsaCalcSwitcher = lazy(() => import("../../features/calculator/switchers/BsaCalcSwitcher"));
const CrcCalcSwitcher = lazy(() => import("../../features/calculator/switchers/CrcCalcSwitcher"));
const IbwCalcSwitcher = lazy(() => import("../../features/calculator/switchers/IbwCalcSwitcher"));
const LbwCalcSwitcher = lazy(() => import("../../features/calculator/switchers/LbwCalcSwitcher"));
const GfrCalcSwitcher = lazy(() => import("../../features/calculator/switchers/GfrCalcSwitcher"));
const AbxCalcSwitcher = lazy(() => import("../../features/calculator/switchers/AbxCalcSwitcher"));

const PAGE_THEME_COLOR = styles.pageThemeColor;

const TemplateMenu = () => {
  const { isAppMode } = useAppModernisation();

  const calculatorsMenuItems = [
    {
      description: "Creatinine clearance calculator for adults",
      link: "/calculator/ccbwcalc",
    },
    {
      description: "Glomerular filtration rate (GFR) calculator for children",
      link: "/calculator/gfrcalc",
    },
    {
      description: "Body surface area calculator",
      link: "/calculator/bsacalc",
    },
    {
      description: "Ideal body weight calculator",
      link: "/calculator/ibwcalc",
    },
    {
      description: "Lean body weight calculator",
      link: "/calculator/lbwcalc",
    },
    ...(isAppMode
      ? [
          {
            description: "Aminoglycoside initial dose calculator for adults",
            link: "/calculator/idcalc",
          },
        ]
      : [
          {
            description: "Amikacin initial dose calculator for adults",
            link: "/calculator/idcalc/amikacin",
          },
          {
            description: "Gentamicin initial dose calculator for adults",
            link: "/calculator/idcalc/gentamicin",
          },
          {
            description: "Tobramycin initial dose calculator for adults",
            link: "/calculator/idcalc/tobramycin",
          },
        ]),
  ];
  const pathObj = {
    "/resources": {
      title: "Resources",
      headColor: PAGE_THEME_COLOR,
      items: [
        {
          description: "Antibiotic prescribing in primary care - summary table",
          summary: true,
          link: `https://ccmsfiles.tg.org.au/${import.meta.env.REACT_APP_CCMS_VERSION}/PDFs/Antibiotic-Summary-table.pdf`,
        },
        {
          description: "Surgical prophylaxis - summary tables",
          summary: true,
          link: `https://ccmsfiles.tg.org.au/${import.meta.env.REACT_APP_CCMS_VERSION}/PDFs/ABG17-SAP-summary-tables-collated.pdf`,
        },
        {
          description: "Patient information and useful PDFs",
          link: "/resources/patientInfo",
        },
        {
          description: "Pregnancy and breastfeeding",
          link: "/app/nav/quicklinks",
        },
        {
          description: "Tables and figures",
          link: "/resources/tables-and-figures",
        },
      ],
    },
    "/calculator": {
      title: "Calculators",
      headColor: PAGE_THEME_COLOR,
      items: calculatorsMenuItems,
    },
    "/calculator/bsacalc": {
      title: "Body surface area calculator",
      headColor: PAGE_THEME_COLOR,
      Component: BsaCalcSwitcher,
      minFontSize: 16,
      maxFontSize: 16,
    },
    "/calculator/ccbwcalc": {
      title: "Creatinine clearance calculator for adults",
      headColor: PAGE_THEME_COLOR,
      Component: CrcCalcSwitcher,
      minFontSize: 16,
      maxFontSize: 16,
    },
    "/calculator/ibwcalc": {
      title: "Ideal body weight calculator",
      headColor: PAGE_THEME_COLOR,
      Component: IbwCalcSwitcher,
      minFontSize: 16,
      maxFontSize: 16,
    },
    "/calculator/lbwcalc": {
      title: "Lean body weight calculator",
      headColor: PAGE_THEME_COLOR,
      Component: LbwCalcSwitcher,
      minFontSize: 16,
      maxFontSize: 16,
    },
    "/calculator/gfrcalc": {
      title: "Glomerular filtration rate (GFR) calculator for children",
      headColor: PAGE_THEME_COLOR,
      Component: GfrCalcSwitcher,
      minFontSize: 16,
      maxFontSize: 16,
    },
    "/calculator/idcalc": {
      title: "Aminoglycoside initial dose calculator for adults",
      headColor: PAGE_THEME_COLOR,
      Component: AbxCalcSwitcher,
      minFontSize: 16,
      maxFontSize: 16,
    },
    "/calculator/idcalc/amikacin": {
      title: "Calculators",
      headColor: PAGE_THEME_COLOR,
      Component: AbxCalcSwitcher,
      extraProps: { currentPath: "Amikacin" },
    },
    "/calculator/idcalc/gentamicin": {
      title: "Calculators",
      headColor: PAGE_THEME_COLOR,
      Component: AbxCalcSwitcher,
      extraProps: { currentPath: "Gentamicin" },
    },
    "/calculator/idcalc/tobramycin": {
      title: "Calculators",
      headColor: PAGE_THEME_COLOR,
      Component: AbxCalcSwitcher,
      extraProps: { currentPath: "Tobramycin" },
    },
    "/calculator/Amikacin": {
      title: isAppMode ? "Aminoglycoside Initial Dose For Adults" : "Calculators",
      headColor: PAGE_THEME_COLOR,
      Component: AbxCalcSwitcher,
      minFontSize: 16,
      maxFontSize: 16,
      extraProps: { currentPath: "Amikacin" },
    },
    "/calculator/Gentamicin": {
      title: isAppMode ? "Aminoglycoside Initial Dose For Adults" : "Calculators",
      headColor: PAGE_THEME_COLOR,
      Component: AbxCalcSwitcher,
      minFontSize: 16,
      maxFontSize: 16,
      extraProps: { currentPath: "Gentamicin" },
    },
    "/calculator/Tobramycin": {
      title: isAppMode ? "Aminoglycoside Initial Dose For Adults" : "Calculators",
      headColor: PAGE_THEME_COLOR,
      Component: AbxCalcSwitcher,
      minFontSize: 16,
      maxFontSize: 16,
      extraProps: { currentPath: "Tobramycin" },
    },
    "/resources/tables-and-figures": {
      title: "Tables & Figures",
      headColor: PAGE_THEME_COLOR,
      Component: TablesAndFigures,
    },
    "/resources/patientInfo": {
      title: "Patient information and useful PDFs",
      headColor: PAGE_THEME_COLOR,
      Component: PatientInfoApp,
    },
    "/resources/:chapterRoot/acknowledgements": {
      title: "Acknowledgements",
      headColor: "#D9F1F3",
      Component: AcknowledgmentForAppMode,
    },
    "/resources/:chapterRoot/expert-groups": {
      title: "Expert group",
      headColor: "#D9F1F3",
      Component: ExpertGroupsForAppMode,
    },
    "resources/faq": {
      title: "FAQs",
      headColor: "#D9F1F3",
      Component: FAQApp,
    },
    "resources/:chapterRoot/updates": {
      title: "Practice-changing updates",
      headColor: "#D9F1F3",
      Component: GuidelineUpdatesPage,
    },
  };

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const matchedKey = Object.keys(pathObj).find((pattern) => {
    const match = matchPath({ path: pattern, end: true }, pathname);
    return match !== null;
  });

  const match = matchPath({ path: matchedKey, end: true }, pathname);
  const chapterRoot = match?.params?.chapterRoot;

  const {
    headColor,
    title,
    items,
    Component,
    extraProps,
    minFontSize = 16,
    maxFontSize = 22,
  } = {
    headColor: "#000000",
    title: "Not found",
    items: [],
    Component: null,
    extraProps: {},
    minFontSize: 14,
    maxFontSize: 22,
    ...pathObj[matchedKey],
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <meta name="theme-color" content={headColor} />
      </Helmet>
      <Suspense fallback={<div className="loader"></div>}>
        <div>
          <MenuBar
            bgColor={headColor}
            title={title}
            onBackClick={goBack}
            minFontSize={minFontSize}
            maxFontSize={maxFontSize}
          />
          <div className="container">
            {items && items.length > 0 ? (
              <>
                {items.map(({ description, summary, link }, i) => {
                  const icon = summary ? "Open" : "Carat";

                  return (
                    <QuickLinkItem
                      rightIcon={icon}
                      title={description}
                      key={`link-${i}`}
                      link={link}
                      documents={summary}
                      className="centered"
                    ></QuickLinkItem>
                  );
                })}
              </>
            ) : null}
            {Component ? <Component chapterRoot={chapterRoot} {...extraProps} /> : null}
          </div>
        </div>
      </Suspense>
    </>
  );
};

export default TemplateMenu;
